/* eslint-disable react/prop-types */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Tag, Empty, Spin, Avatar } from 'antd';
import dayjs from 'dayjs';
import { queryXplanCourseDetail } from './api';
import KeypointPlayer from './KeypointPlayer';
import './ActionOverviewDialog.less';

const formatCourseDuration = (durationSeconds, fallbackDurationSeconds) => {
  const totalSeconds = durationSeconds ?? fallbackDurationSeconds;
  if (totalSeconds === undefined || totalSeconds === null) return '-';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds} 秒`;
  if (seconds === 0) return `${minutes} 分钟`;
  return `${minutes} 分钟 ${seconds} 秒`;
};

const formatRatio = (ratio) => {
  if (ratio === undefined || ratio === null || ratio === '') return '-';
  return `${ratio}%`;
};

const getReachStandardValue = (detail, record) => {
  if (detail?.meetStandard !== undefined && detail?.meetStandard !== null) {
    return { value: detail.meetStandard };
  }
  if (record?.meetStandard !== undefined && record?.meetStandard !== null) {
    return { value: record.meetStandard };
  }
  return { value: false };
};

const getAttendTypeDesc = (type) => {
  return type === 0 ? '进入' : type === 1 ? '离开' : '未知';
};

const getPersonTypeDesc = (type) => {
  return type === 0 ? '用户' : type === 1 ? '教练' : '未知';
};

const ActionOverviewDialog = ({ visible, onClose, record }) => {
  const [loading, setLoading] = useState(false);
  const [courseOverview, setCourseOverview] = useState(null);
  const [attendData, setAttendData] = useState([]);
  const [classCommentInfoList, setClassCommentInfoList] = useState([]);

  const allActions = useMemo(
    () =>
      classCommentInfoList.flatMap((stage) =>
        (stage.actionRemarkDetails || []).map((item) => ({
          ...item,
          phaseName: stage.stageName || '',
        })),
      ),
    [classCommentInfoList],
  );

  const attendColumns = [
    { title: '时间', dataIndex: 'operateTime', key: 'operateTime' },
    { title: '人员昵称', dataIndex: 'personName', key: 'personName' },
    { title: '人员类型', dataIndex: 'personTypeDesc', key: 'personTypeDesc' },
    { title: '进出类型', dataIndex: 'attendTypeDesc', key: 'attendTypeDesc' },
    { title: '上课终端类型', dataIndex: 'terminalType', key: 'terminalType' },
    { title: '微信版本号', dataIndex: 'wxVersion', key: 'wxVersion' },
  ];

  const resetData = () => {
    setCourseOverview(null);
    setAttendData([]);
    setClassCommentInfoList([]);
  };

  const fetchCourseDetail = async (roomId) => {
    if (!roomId) return;
    try {
      setLoading(true);
      const res = await queryXplanCourseDetail({
        requestData: {
          roomId,
        },
      });
      const detailData = res?.data || {};
      setCourseOverview(detailData || {});

      // 处理出勤记录
      if (Array.isArray(detailData?.attendanceList)) {
        setAttendData(
          detailData.attendanceList.map((item) => ({
            operateTime: item.operateTime
              ? dayjs(item.operateTime).format('YYYY-MM-DD HH:mm:ss')
              : '-',
            personName: item.personName || '-',
            personTypeDesc: getPersonTypeDesc(item.personType),
            attendTypeDesc: getAttendTypeDesc(item.attendType),
            terminalType: item.terminalType || '-',
            wxVersion: item.wxVersion || '-',
          })),
        );
      } else {
        setAttendData([]);
      }

      // 处理动作列表，按 phaseId 分组
      if (Array.isArray(detailData?.actionList)) {
        const phaseMap = new Map();
        detailData.actionList.forEach((action) => {
          const phaseId = action.phaseId;
          if (!phaseMap.has(phaseId)) {
            phaseMap.set(phaseId, []);
          }
          phaseMap.get(phaseId).push({
            actionName: action.actionName || '-',
            isValid: action.isValid,
            framesUrl: action.actionKeypointsUrl || '',
            isJsonl: action.isJsonl || false,
          });
        });
        setClassCommentInfoList(
          Array.from(phaseMap.entries()).map(([phaseId, actions]) => ({
            stageName: `训练阶段 ${phaseId}`,
            actionRemarkDetails: actions,
          })),
        );
      } else {
        setClassCommentInfoList([]);
      }
    } catch (error) {
      console.error('获取课程详情失败:', error);
      resetData();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && record) {
      const roomId = record.roomId || record.id;
      if (roomId) {
        fetchCourseDetail(roomId);
      } else {
        resetData();
      }
    } else {
      resetData();
    }
  }, [visible, record]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title="课程详情"
      width={1000}
      footer={null}
      destroyOnClose
      centered
      className="action-overview-dialog"
    >
      <Spin spinning={loading}>
        <div className="action-overview-content">
          <div className="info-card">
            <div className="info-header">
              <div className="course-name">
                {courseOverview?.courseName ||
                  record?.courseName ||
                  record?.title ||
                  '未知课表'}
              </div>
            </div>

            <div className="info-body">
              {record?.coachNickName && (
                <div className="info-item">
                  <div className="label">指导教练：</div>
                  <div className="value coach-info">
                    {record?.coachHeadImg && (
                      <Avatar
                        size={24}
                        src={record.coachHeadImg}
                        className="coach-avatar"
                      />
                    )}
                    <span>{record?.coachNickName || '未知教练'}</span>
                  </div>
                </div>
              )}

              <div className="info-item">
                <div className="label">课表ID：</div>
                <div className="value">
                  {courseOverview?.roomId ||
                    record?.roomId ||
                    record?.id ||
                    '-'}
                </div>
              </div>

              <div className="info-item">
                <div className="label">上课时间：</div>
                <div className="value">
                  {courseOverview?.classTime || record?.classTime || '-'}
                </div>
              </div>

              <div className="info-item">
                <div className="label">课程时长：</div>
                <div className="value">
                  {formatCourseDuration(
                    courseOverview?.durationSeconds,
                    record?.durationSeconds,
                  )}
                </div>
              </div>

              <div className="info-item">
                <div className="label">课程状态：</div>
                <div className="value">
                  {courseOverview?.courseStatus || '-'}
                </div>
              </div>

              <div className="info-item">
                <div className="label">入镜占比：</div>
                <div className="value">
                  {formatRatio(courseOverview?.inCameraRatio)}
                </div>
              </div>

              <div className="info-item">
                <div className="label">是否达标：</div>
                <div className="value">
                  <Tag
                    color={
                      getReachStandardValue(courseOverview, record)?.value
                        ? 'success'
                        : 'error'
                    }
                  >
                    {getReachStandardValue(courseOverview, record)?.value
                      ? '达标'
                      : '未达标'}
                  </Tag>
                </div>
              </div>

              {record?.address && (
                <div className="info-item full-width">
                  <div className="label">门店地址：</div>
                  <div className="value">{record?.address}</div>
                </div>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-title">出勤记录</div>
            <div className="table-wrapper">
              <Table
                dataSource={attendData}
                columns={attendColumns}
                rowKey={(record, index) => `${record.operateTime}-${index}`}
                size="small"
                pagination={false}
                locale={{
                  emptyText: (
                    <Empty
                      image="https://prd-cos.litta.cn/litta-resources/litta-h5/common/icon_empty.png"
                      description="暂无数据"
                    />
                  ),
                }}
              />
            </div>
          </div>

          <div className="section-card">
            <div className="section-title">
              动作概览
              <span className="remark">
                (为避免用户隐私泄露，已自动提取关键骨骼点，代表用户已入镜)
              </span>
            </div>
            {allActions.length === 0 ? (
              <div className="empty-state">
                <Empty
                  image="https://prd-cos.litta.cn/litta-resources/litta-h5/common/icon_empty.png"
                  description="暂无数据"
                />
              </div>
            ) : (
              <div className="action-grid">
                {allActions.map((item, index) => (
                  <div
                    key={`${item.phaseName}-${item.actionName}-${index}`}
                    className="action-item"
                  >
                    <div className="skeleton-bg">
                      {item.framesUrl ? (
                        <KeypointPlayer
                          dataUrl={item.framesUrl}
                          loop={true}
                          autoPlay={true}
                          onlyShow={true}
                        />
                      ) : (
                        <div className="no-skeleton">
                          <span>暂无动作演示</span>
                        </div>
                      )}
                    </div>
                    <div className="action-info">
                      <div className="action-name">{item.actionName}</div>
                      <span
                        className={`action-status-dot ${
                          item.isValid ? 'valid' : 'invalid'
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default ActionOverviewDialog;
